import type { ImpersonationResponse, RevertImpersonationResponse } from '@/types/impersonation';
import { api } from './api';

// ========================================
// TIPOS - Gestión de Asesores
// ========================================

export interface AsesorSysadmin {
  asesorId: number;
  nombre: string;
  correo: string;
  telefono: string;
  estado: boolean;
  cantidadEmpresas: number;
  rol: 'ADMIN' | 'ASESOR' | 'SYSADMIN';
  urlFormularioAudio?: string | null;
}

interface MigrarAsociacionesResponse {
  success: true;
  message: string;
  ternasCreadas: number;
  gruposEncontrados: number;
  requisitosAsociados: number;
  asociacionesEliminadas: number;
}

export interface ListarAsesoresResponse {
  success: boolean;
  asesores: AsesorSysadmin[];
}

export interface AsesorDetailResponse {
  success: boolean;
  asesor: AsesorSysadmin;
}

export interface CrearAsesorRequest {
  nombre: string;
  correo: string;
  telefono: string;
  rol?: 'ADMIN' | 'ASESOR' | 'SYSADMIN';
}

export interface ActualizarAsesorRequest {
  nombre?: string;
  correo?: string;
  telefono?: string;
  rol?: 'ADMIN' | 'ASESOR' | 'SYSADMIN';
  estado?: boolean;
  urlFormularioAudio?: string | null;
}

export interface AsesorFormUrlsResponse {
  success: boolean;
  viewUrl: string | null;
  editUrl: string | null;
  raw: string | null;
}

// ========================================
// TIPOS - Gestión de Empresas (todas)
// ========================================

export interface EmpresaSysadmin {
  nitId: string;
  nombre: string;
  asesorId: number | null;
  asesor: { nombre: string } | null;
  combinacionNtcId: number | null;
  combinacionNtc: { nombre: string } | null;
  estado: boolean;
  carpetaId?: string | null;
  cantidadIndicadores?: number;
  cantidadCumplen?: number;
  cantidadNoCumplen?: number;
  cantidadPendientes?: number;
}

export interface ListarEmpresasSysadminResponse {
  success: boolean;
  empresas: EmpresaSysadmin[];
}

export interface EmpresaPorCombinacion {
  nitId: string;
  nombre: string;
  asesorId: number | null;
  asesor: {
    nombre: string;
    correo: string;
  } | null;
}

export interface ListarEmpresasPorCombinacionResponse {
  success: boolean;
  empresas: EmpresaPorCombinacion[];
}

// ========================================
// TIPOS - Gestión de Requisitos por Empresa
// ========================================

export interface NtcDisponible {
  ntcId: number;
  nombre: string;
  totalRequisitos: number;
}

export interface CombinacionNtcInfo {
  combinacionId: number;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  ntcs: NtcDisponible[];
  hasLegacyData?: boolean;
}

export interface RequisitoSysadmin {
  requisitoId: string;
  ntcId: number;
  ntcNombre: string;
  capitulo: string;
  numeral: string;
  descripcion: string;
  posibleEvidencia: string | null;
}

export interface RequisitoEmpresaEstado {
  requisitoId: string;
  ntcId: number;
  ntcNombre: string;
  capitulo: string;
  numeral: string;
  descripcion: string;
  asociado: boolean; // si está asociado a la empresa
  calificacion: 'C' | 'NC' | 'CP' | 'NA' | null; // null si no está asociado
}

export interface ListarNtcsDisponiblesResponse {
  success: boolean;
  ntcs: NtcDisponible[];
}

export interface ListarCombinacionesResponse {
  success: boolean;
  combinaciones: CombinacionNtcInfo[];
}

export interface ListarRequisitosDisponiblesResponse {
  success: boolean;
  requisitos: RequisitoSysadmin[];
}

export interface ListarRequisitosEmpresaResponse {
  success: boolean;
  requisitos: RequisitoEmpresaEstado[];
}

export interface AsignarCombinacionResponse {
  success: boolean;
  message: string;
}

export interface AsignarAsesorResponse {
  success: boolean;
  message: string;
}

export interface GenericResponse {
  success: boolean;
  message: string;
}

export interface CreateEmpresaData {
  nitId: string;
  nombre: string;
  departamento: string;
  municipio: string;
  ubicacion?: string;
  correoEmpresa: string;
  representanteLegal?: string;
  rnt?: string;
  rntCategoria?: string;
  rntSubCategoria?: string;
  combinacionNtcId?: number;
  asesorId?: number;
}

export interface CreateEmpresaResponse {
  success: boolean;
  message: string;
  empresa: {
    nitId: string;
    nombre: string;
    combinacionNtcId: number | null;
    asesorId: number | null;
    requisitosCreados: number;
  };
}

export interface CambiarCombinacionNtcResponse {
  success: boolean;
  message: string;
  requisitosCreados: number;
  asesoriasFinalizadas: number;
}

// ========================================
// TIPOS - Asociaciones entre Requisitos
// ========================================

export interface AsociacionNtcInfo {
  ntcId: number;
  nombre: string;
  orden: number;
}

export interface AsociacionCombinacionInfo {
  combinacionId: number;
  nombre: string;
  ntcs: AsociacionNtcInfo[];
  hasLegacyData?: boolean;
}

export interface RequisitoAsociacion {
  requisitoId: string;
  ntcId: number;
  capitulo: string;
  numeral: string;
  descripcion: string;
}

export interface ListarAsociacionesResponse {
  success: boolean;
  combinacion: AsociacionCombinacionInfo;
  requisitos: RequisitoAsociacion[];
  asociaciones: Record<string, string[]>;
}

export interface TernaDTO {
  id: number;
  orden: number;
  grupo: string | null;
  requisitoC1Id: string | null;
  requisitoC2Id: string | null;
  requisitoC3Id: string | null;
}

export interface AgruparTernasRequest {
  ternaIds: number[];
}

export interface AgruparTernasResponse {
  success: boolean;
  message: string;
  grupo: string;
  ternasAfectadas: number;
}

export interface CrearTernaRequest {
  requisitoC1Id?: string | null;
  requisitoC2Id?: string | null;
  requisitoC3Id?: string | null;
  orden?: number;
  grupo?: string | null;
}

export interface CrearTernaResponse {
  success: boolean;
  message: string;
  terna: TernaDTO;
}

export interface ListarTernasResponse {
  success: boolean;
  combinacion: AsociacionCombinacionInfo;
  requisitos: RequisitoAsociacion[];
  ternas: TernaDTO[];
  requisitosSinTerna: string[];
}

export interface CrearAsociacionRequest {
  requisitoOrigenId: string;
  requisitoDestinoId: string;
  observaciones?: string;
}

export interface CrearAsociacionResponse {
  success: boolean;
  message: string;
  asociacion?: {
    id: number;
    requisitoOrigenId: string;
    requisitoDestinoId: string;
    observaciones: string | null;
  };
}

export interface CrearAsociacionesBatchRequest {
  asociaciones: { origenId: string; destinoId: string; observaciones?: string }[];
}

export interface CrearAsociacionesBatchResponse {
  success: boolean;
  message: string;
  creadas: number;
  duplicadas: number;
  errores: string[];
}

export interface EliminarAsociacionesBatchRequest {
  pares: { origenId: string; destinoId: string }[];
}

export interface EliminarAsociacionesBatchResponse {
  success: boolean;
  message: string;
  eliminadas: number;
}

export interface BuscarRequisitosResponse {
  success: boolean;
  requisitos: (RequisitoAsociacion & { ntcNombre: string })[];
}

export interface AsociacionTernaRequest {
  requisitoAId: string;
  requisitoBId: string;
}

export interface TernaReordenarRequest {
  id: number;
  orden: number;
}

// ========================================
// TIPOS - Gestión de Evidencias
// ========================================

export interface EvidenciaSysadminDTO {
  evidenciaId: number;
  nombre: string;
  descripcion: string | null;
  fechaAdicion: string;
  url: string;
  tipoArchivo: string | null;
  estado: string;
  asesoriaId: number;
  empresaId: string;
  empresaNombre: string;
  asesorNombre: string;
}

export interface EvidenciasFilter {
  empresaId?: string;
  asesorId?: number;
}

export interface ListarEvidenciasSysadminResponse {
  success: boolean;
  evidencias: EvidenciaSysadminDTO[];
}

// ========================================
// TIPOS - Gestión de Asesorías (todas las empresas)
// ========================================

export interface AsesoriaSysadminDTO {
  asesoriaId: number;
  empresaId: string;
  empresaNombre: string;
  asesorId: number;
  asesorNombre: string;
  fechaInicio: string;
  fechaFin: string | null;
  estado: string;
  horas: number;
  objetivo: string;
  recomendaciones: string | null;
  orden: number | null;
  autoAprobar: boolean;
  audiosCount: number;
  evidenciasCount: number;
  evaluacionesCount: number;
}

export interface AsesoriasSysadminFilter {
  empresaId?: string;
  asesorId?: number;
  estado?: 'EN_CURSO' | 'EN_REVISION' | 'FINALIZADA' | 'REQUIERE_CORRECCIONES' | 'REVERTIDA' | 'SIN_EVALUAR';
}

export interface ListarAsesoriasSysadminResponse {
  success: boolean;
  asesorias: AsesoriaSysadminDTO[];
}

// ========================================
// TIPOS - Gestión de Audios
// ========================================

export interface AudioSysadminDTO {
  audioId: number;
  empresaId: string;
  asesoriaId: number;
  asesorId: number;
  fechaGrabacion: string;
  adjunteDelAudio: string;
  duracionSeg: number;
  email: string | null;
  estado?: string;
}

export interface ListarAudiosSysadminResponse {
  success: boolean;
  audios: AudioSysadminDTO[];
}

export interface AudioEliminadoReasignacionDTO {
  audioId: number;
  nitEmpresa: string;
  nombreEmpresa: string;
  asesoriaId: number;
  ordenAsesoria: number | null;
  fechaGrabacion: string;
  duracionSeg: number;
  observacion: string | null;
}

export interface ListarAudiosEliminadosReasignacionResponse {
  success: boolean;
  audios: AudioEliminadoReasignacionDTO[];
}

export interface CompartirAudioRequest {
  correos: string[];
}

export interface CompartirAudioResponse {
  success: boolean;
  message: string;
  exitosos: string[];
  fallidos: string[];
}

export interface AudioDuplicadoItemDTO {
  audioId: number;
  empresaId: string;
  empresaNombre: string;
  asesorId: number;
  asesorNombre: string;
  asesoriaId: number;
  asesoriaEstado: string;
  fechaGrabacion: string;
  duracionSeg: number;
  duracionFormatted: string;
  estado: string;
  adjunteDelAudio: string;
  driveUrl: string;
}

export interface GrupoAudiosDuplicadosDTO {
  duracionSeg: number;
  duracionFormatted: string;
  totalAudios: number;
  audios: AudioDuplicadoItemDTO[];
}

export interface EmpresaAudiosDuplicadosDTO {
  nit: string;
  nombre: string;
  asesorTitular: string;
  totalAudios: number;
  audios: Array<
    AudioDuplicadoItemDTO & {
      coincidencias: Array<{
        audioId: number;
        empresaId: string;
        empresaNombre: string;
        asesorNombre: string;
        asesoriaId: number;
        asesoriaEstado: string;
        duracionSeg: number;
        duracionFormatted: string;
        driveUrl: string;
      }>;
    }
  >;
}

export interface AudiosDuplicadosResponse {
  success: boolean;
  resumen: {
    totalAudios: number;
    totalGrupos: number;
    totalEmpresasAfectadas: number;
    totalAudiosCoincidentes: number;
  };
  casoEspecial: {
    paresClonados: Array<{
      parNum: number;
      duracionSeg: number;
      duracionFormatted: string;
      audioA: {
        audioId: number;
        empresa: string;
        nit: string;
        asesor: string;
        asesoriaId: number;
        driveUrl: string;
      } | null;
      audioB: {
        audioId: number;
        empresa: string;
        nit: string;
        asesor: string;
        asesoriaId: number;
        driveUrl: string;
      } | null;
    }>;
    jornadaCompletaA2: Array<{
      audioId: number;
      duracionSeg: number;
      duracionFormatted: string;
      fechaGrabacion: string;
      driveUrl: string;
    }>;
  };
  grupos: GrupoAudiosDuplicadosDTO[];
  empresas: EmpresaAudiosDuplicadosDTO[];
}

// ========================================
// TIPOS - Drive
// ========================================

export interface CopyDriveFileRequest {
  destinationFolderId: string;
  newFileName: string;
  emails: string[];
  sourceFileId?: string;
  role?: 'reader' | 'writer' | 'commenter' | 'owner';
  delayMs?: number;
}

export interface CopyDriveFileResponse {
  success: boolean;
  message: string;
  file: {
    id: string;
    url: string;
  };
  sharing: {
    succeeded: string[];
    failed: { email: string; error: string }[];
  };
}

// ========================================
// TIPOS - Monitoreo de Sistema (Logs)
// ========================================

export interface GoogleJobLog {
  id: number;
  service: string;
  operation: string;
  payload: unknown;
  status: 'PENDING' | 'IN_PROGRESS' | 'SUCCEEDED' | 'FAILED';
  attempts: number;
  maxAttempts: number;
  nextRun: string;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListarGoogleJobsResponse {
  success: boolean;
  googleJobs: GoogleJobLog[];
}

// ========================================
// SERVICIO
// ========================================

export const sysadminService = {
  // --- Logs de Sistema ---
  async listarGoogleJobs(): Promise<ListarGoogleJobsResponse> {
    return api.get<ListarGoogleJobsResponse>('/sysadmin/google-jobs');
  },

  // --- Asesores ---
  async listarAsesores(): Promise<ListarAsesoresResponse> {
    return api.get<ListarAsesoresResponse>('/sysadmin/asesores');
  },

  async crearAsesor(data: CrearAsesorRequest): Promise<AsesorDetailResponse> {
    return api.post<AsesorDetailResponse>('/sysadmin/asesores', data);
  },

  async actualizarAsesor(asesorId: number, data: ActualizarAsesorRequest): Promise<AsesorDetailResponse> {
    return api.patch<AsesorDetailResponse>(`/sysadmin/asesores/${asesorId}`, data);
  },

  async toggleEstadoAsesor(asesorId: number): Promise<GenericResponse> {
    return api.patch<GenericResponse>(`/sysadmin/asesores/${asesorId}/toggle-estado`);
  },

  async getFormUrlsAsesor(asesorId: number): Promise<AsesorFormUrlsResponse> {
    return api.get<AsesorFormUrlsResponse>(`/sysadmin/asesores/${asesorId}/form-urls`);
  },

  // --- Empresas (todas) ---
  async listarTodasEmpresas(): Promise<ListarEmpresasSysadminResponse> {
    return api.get<ListarEmpresasSysadminResponse>('/sysadmin/empresas');
  },

  async asignarAsesorEmpresa(nit: string, asesorId: number | null): Promise<AsignarAsesorResponse> {
    return api.patch<AsignarAsesorResponse>(`/sysadmin/empresas/${nit}/asesor`, { asesorId });
  },

  // --- Combinaciones NTC ---
  async listarCombinaciones(): Promise<ListarCombinacionesResponse> {
    return api.get<ListarCombinacionesResponse>('/sysadmin/combinaciones-ntc');
  },

  async listarEmpresasPorCombinacion(combinacionId: number): Promise<ListarEmpresasPorCombinacionResponse> {
    return api.get<ListarEmpresasPorCombinacionResponse>(`/sysadmin/combinaciones-ntc/${combinacionId}/empresas`);
  },

  async asignarCombinacion(nit: string, combinacionNtcId: number | null): Promise<AsignarCombinacionResponse> {
    return api.patch<AsignarCombinacionResponse>(`/sysadmin/empresas/${nit}/combinacion-ntc`, { combinacionNtcId });
  },

  async crearEmpresa(data: CreateEmpresaData): Promise<CreateEmpresaResponse> {
    return api.post<CreateEmpresaResponse>('/sysadmin/empresas', data);
  },

  async eliminarEmpresa(nit: string): Promise<GenericResponse> {
    return api.delete<GenericResponse>(`/sysadmin/empresas/${nit}`);
  },

  async reactivarEmpresa(nit: string): Promise<GenericResponse> {
    return api.patch<GenericResponse>(`/sysadmin/empresas/${nit}/reactivar`);
  },

  async cambiarCombinacionNtc(nit: string, combinacionNtcId: number | null): Promise<CambiarCombinacionNtcResponse> {
    return api.put<CambiarCombinacionNtcResponse>(`/sysadmin/empresas/${nit}/cambiar-combinacion-ntc`, { combinacionNtcId });
  },

  // --- NTCs disponibles ---
  async listarNtcsDisponibles(): Promise<ListarNtcsDisponiblesResponse> {
    return api.get<ListarNtcsDisponiblesResponse>('/sysadmin/ntcs');
  },

  // --- Requisitos por empresa ---
  async listarRequisitosDisponibles(ntcId?: number): Promise<ListarRequisitosDisponiblesResponse> {
    const query = ntcId ? `?ntcId=${ntcId}` : '';
    return api.get<ListarRequisitosDisponiblesResponse>(`/sysadmin/requisitos${query}`);
  },

  async listarRequisitosEmpresa(nit: string, ntcId?: number): Promise<ListarRequisitosEmpresaResponse> {
    const query = ntcId ? `?ntcId=${ntcId}` : '';
    return api.get<ListarRequisitosEmpresaResponse>(`/sysadmin/empresas/${nit}/requisitos${query}`);
  },

  async asociarRequisito(nit: string, requisitoId: string): Promise<GenericResponse> {
    return api.post<GenericResponse>(`/sysadmin/empresas/${nit}/requisitos`, { requisitoId });
  },

  async desasociarRequisito(nit: string, requisitoId: string): Promise<GenericResponse> {
    return api.delete<GenericResponse>(`/sysadmin/empresas/${nit}/requisitos/${requisitoId}`);
  },

  async asociarRequisitosBatch(nit: string, requisitoIds: string[]): Promise<GenericResponse> {
    return api.post<GenericResponse>(`/sysadmin/empresas/${nit}/requisitos/batch`, { requisitoIds });
  },

  async desasociarRequisitosBatch(nit: string, requisitoIds: string[]): Promise<GenericResponse> {
    return api.post<GenericResponse>(`/sysadmin/empresas/${nit}/requisitos/batch-remove`, { requisitoIds });
  },

  // --- Asociaciones entre requisitos ---
  async listarAsociaciones(combinacionId: number): Promise<ListarAsociacionesResponse> {
    return api.get<ListarAsociacionesResponse>(`/sysadmin/combinaciones-ntc/${combinacionId}/asociaciones`);
  },

  async listarTernas(combinacionId: number): Promise<ListarTernasResponse> {
    return api.get<ListarTernasResponse>(`/sysadmin/combinaciones-ntc/${combinacionId}/ternas`);
  },

  async crearAsociacion(data: CrearAsociacionRequest): Promise<CrearAsociacionResponse> {
    return api.post<CrearAsociacionResponse>('/sysadmin/asociaciones', data);
  },

  async crearAsociacionesBatch(data: CrearAsociacionesBatchRequest): Promise<CrearAsociacionesBatchResponse> {
    return api.post<CrearAsociacionesBatchResponse>('/sysadmin/asociaciones/batch', data);
  },

  async eliminarAsociacion(asociacionId: number): Promise<GenericResponse> {
    return api.delete<GenericResponse>(`/sysadmin/asociaciones/${asociacionId}`);
  },

  async eliminarAsociacionesBatch(data: EliminarAsociacionesBatchRequest): Promise<EliminarAsociacionesBatchResponse> {
    return api.post<EliminarAsociacionesBatchResponse>('/sysadmin/asociaciones/batch-remove', data);
  },

  async buscarRequisitos(q: string, ntcId?: number, limit?: number): Promise<BuscarRequisitosResponse> {
    const params = new URLSearchParams({ q });
    if (ntcId) params.set('ntcId', ntcId.toString());
    if (limit) params.set('limit', limit.toString());
    return api.get<BuscarRequisitosResponse>(`/sysadmin/requisitos/buscar?${params.toString()}`);
  },

  // --- Crear Requisito ---
  async crearRequisito(data: import('@/types/types').CrearRequisitoRequest): Promise<import('@/types/types').CrearRequisitoResponse> {
    return api.post<import('@/types/types').CrearRequisitoResponse>('/sysadmin/requisitos', data);
  },

  async asociarEnTerna(combinacionId: number, data: AsociacionTernaRequest): Promise<GenericResponse> {
    return api.post<GenericResponse>(`/sysadmin/combinaciones-ntc/${combinacionId}/asociaciones`, data);
  },

  async desasociarDeTerna(combinacionId: number, data: AsociacionTernaRequest): Promise<GenericResponse> {
    return api.delete<GenericResponse>(`/sysadmin/combinaciones-ntc/${combinacionId}/asociaciones`, data);
  },

  async reordenarTernas(combinacionId: number, data: { ordenes: TernaReordenarRequest[] }): Promise<GenericResponse> {
    return api.put<GenericResponse>(`/sysadmin/combinaciones-ntc/${combinacionId}/ternas/reordenar`, data);
  },

  async eliminarTerna(ternaId: number): Promise<GenericResponse> {
    return api.delete<GenericResponse>(`/sysadmin/ternas/${ternaId}`);
  },

  async patchTerna(
    ternaId: number,
    data: {
      requisitoC1Id?: string | null;
      requisitoC2Id?: string | null;
      requisitoC3Id?: string | null;
      grupo?: string | null;
      orden?: number;
    }
  ): Promise<GenericResponse> {
    return api.patch<GenericResponse>(`/sysadmin/ternas/${ternaId}`, data);
  },

  async crearTerna(combinacionId: number, data: CrearTernaRequest): Promise<CrearTernaResponse> {
    return api.post<CrearTernaResponse>(`/sysadmin/combinaciones-ntc/${combinacionId}/ternas`, data);
  },

  async insertarTerna(
    combinacionId: number,
    data: { requisitoId: string; columnaOrden: number; insertAtOrden: number }
  ): Promise<CrearTernaResponse> {
    return api.post<CrearTernaResponse>(`/sysadmin/combinaciones-ntc/${combinacionId}/ternas/insert`, data);
  },

  async agruparTernas(combinacionId: number, data: AgruparTernasRequest): Promise<AgruparTernasResponse> {
    return api.post<AgruparTernasResponse>(`/sysadmin/combinaciones-ntc/${combinacionId}/ternas/agrupar`, data);
  },

  async desagruparTerna(combinacionId: number, ternaId: number): Promise<GenericResponse> {
    return api.post<GenericResponse>(`/sysadmin/combinaciones-ntc/${combinacionId}/ternas/${ternaId}/desagrupar`);
  },

  // --- Actualizar Requisito ---
  async actualizarRequisito(
    requisitoId: string,
    data: import('@/types/types').ActualizarRequisitoRequest
  ): Promise<import('@/types/types').ActualizarRequisitoResponse> {
    return api.patch<import('@/types/types').ActualizarRequisitoResponse>(`/sysadmin/requisitos/${requisitoId}`, data);
  },

  async eliminarRequisito(requisitoId: string): Promise<GenericResponse> {
    return api.delete<GenericResponse>(`/sysadmin/requisitos/${requisitoId}`);
  },

  async viabilidadEliminacionRequisito(requisitoId: string): Promise<import('@/types/types').ViabilidadRequisitoResponse> {
    return api.get<import('@/types/types').ViabilidadRequisitoResponse>(`/sysadmin/requisitos/${requisitoId}/viabilidad-eliminacion`);
  },

  async getRequisitoAnalisis(requisitoId: string): Promise<import('@/types/types').AnalisisDetalladoRequisitoResponse> {
    return api.get<import('@/types/types').AnalisisDetalladoRequisitoResponse>(`/sysadmin/requisitos/${requisitoId}/analisis-detallado`);
  },

  async migrarDatosRequisito(
    data: import('@/types/types').MigrarRequisitoRequest
  ): Promise<import('@/types/types').MigrarRequisitoResponse> {
    return api.patch<import('@/types/types').MigrarRequisitoResponse>('/sysadmin/requisitos/migrar-datos', data);
  },

  // --- Impersonación ---
  async impersonateAsesor(asesorId: number): Promise<ImpersonationResponse> {
    return api.post<ImpersonationResponse>(`/sysadmin/impersonate/${asesorId}`);
  },

  async revertImpersonation(): Promise<RevertImpersonationResponse> {
    return api.post<RevertImpersonationResponse>('/impersonation/revert');
  },

  async switchToAdmin(): Promise<{ success: boolean; token: string }> {
    return api.post<{ success: boolean; token: string }>('/sysadmin/switch-to-admin');
  },

  async migrarAsociacionesATernas(combinacionId: number): Promise<MigrarAsociacionesResponse> {
    return api.post(`/sysadmin/combinaciones-ntc/${combinacionId}/asociaciones/migrar-a-ternas`);
  },

  async eliminarAsociacionesLegacyCombinacion(combinacionId: number): Promise<GenericResponse> {
    return api.delete<GenericResponse>(`/sysadmin/combinaciones/${combinacionId}/asociaciones-legacy`);
  },

  // --- Evidencias ---
  async listarEvidencias(filtros?: EvidenciasFilter): Promise<ListarEvidenciasSysadminResponse> {
    const params = new URLSearchParams();
    if (filtros?.empresaId) params.append('empresaId', filtros.empresaId);
    if (filtros?.asesorId) params.append('asesorId', filtros.asesorId.toString());

    const queryString = params.toString();
    const url = `/sysadmin/evidencias${queryString ? `?${queryString}` : ''}`;

    return api.get<ListarEvidenciasSysadminResponse>(url);
  },

  async eliminarEvidencia(evidenciaId: number): Promise<GenericResponse> {
    return api.delete<GenericResponse>(`/sysadmin/evidencias/${evidenciaId}`);
  },

  // --- Asesorías (todas las empresas) ---
  async listarAsesorias(filtros?: AsesoriasSysadminFilter): Promise<ListarAsesoriasSysadminResponse> {
    const params = new URLSearchParams();
    if (filtros?.empresaId) params.append('empresaId', filtros.empresaId);
    if (filtros?.asesorId) params.append('asesorId', filtros.asesorId.toString());
    if (filtros?.estado) params.append('estado', filtros.estado);

    const queryString = params.toString();
    const url = `/sysadmin/asesorias${queryString ? `?${queryString}` : ''}`;

    return api.get<ListarAsesoriasSysadminResponse>(url);
  },

  // --- Audios ---
  async listarAudios(): Promise<ListarAudiosSysadminResponse> {
    return api.get<ListarAudiosSysadminResponse>('/sysadmin/audios');
  },

  async eliminarAudio(audioId: number): Promise<GenericResponse> {
    return api.delete<GenericResponse>(`/sysadmin/audios/${audioId}`);
  },

  async listarAudiosEliminadosPorReasignacion(): Promise<ListarAudiosEliminadosReasignacionResponse> {
    return api.get<ListarAudiosEliminadosReasignacionResponse>('/sysadmin/audios/eliminados-por-reasignacion');
  },

  async compartirAudio(audioId: number, data: CompartirAudioRequest): Promise<CompartirAudioResponse> {
    return api.post<CompartirAudioResponse>(`/sysadmin/audios/${audioId}/compartir`, data);
  },

  async obtenerAudiosDuplicados(): Promise<AudiosDuplicadosResponse> {
    return api.get<AudiosDuplicadosResponse>('/sysadmin/audios/duplicados');
  },

  /** Auditoría (solo lectura) de nombres de audios contra la carpeta "Audios" de cada empresa. */
  async auditarNombresAudios(nit?: string): Promise<AuditoriaNombresAudiosResponse> {
    const qs = nit ? `?nit=${encodeURIComponent(nit)}` : '';
    return api.get<AuditoriaNombresAudiosResponse>(`/sysadmin/audios/nombres/auditoria${qs}`);
  },

  async descargarExcelAudiosDuplicados(): Promise<Blob> {
    return api.getBlob('/sysadmin/audios/duplicados/exportar');
  },

  // --- Auditoría (Edición Administrativa) ---
  async actualizarAsesoriaAuditoria(asesoriaId: number, data: Record<string, unknown>): Promise<GenericResponse> {
    return api.patch<GenericResponse>(`/sysadmin/auditoria/asesorias/${asesoriaId}`, data);
  },

  /** Crea un audio de PRUEBA (datos dummy) para una asesoría, para testear el flujo de corrección
   * de audios sin depender del webhook del Google Form. Solo sysadmin/dev. */
  async crearAudioPrueba(asesoriaId: number, data?: { adjunteDelAudio?: string; duracionSeg?: number }): Promise<GenericResponse> {
    return api.post<GenericResponse>(`/sysadmin/asesorias/${asesoriaId}/audios-prueba`, data ?? {});
  },

  async actualizarEvaluacionAuditoria(evaluacionId: number, data: Record<string, unknown>): Promise<GenericResponse> {
    return api.patch<GenericResponse>(`/sysadmin/auditoria/evaluaciones/${evaluacionId}`, data);
  },

  async actualizarAudioAuditoria(audioId: number, data: Record<string, unknown>): Promise<GenericResponse> {
    return api.patch<GenericResponse>(`/sysadmin/auditoria/audios/${audioId}`, data);
  },

  // --- Cumplimiento NTC (recalculo masivo) ---
  async recalcularCumplimientoTodasNTCS(): Promise<{ success: boolean; updated: number }> {
    return api.post<{ success: boolean; updated: number }>('/sysadmin/cumplimiento/recalcular');
  },

  async sincronizarCumplimiento(): Promise<{ success: boolean; processed: number }> {
    return api.post<{ success: boolean; processed: number }>('/sysadmin/cumplimiento/sincronizar');
  },

  async sincronizarEmpresa(nit: string): Promise<{ success: boolean; nit: string }> {
    return api.post<{ success: boolean; nit: string }>(`/sysadmin/cumplimiento/sincronizar/${nit}`);
  },

  async obtenerEmpresasDesincronizadas(): Promise<ListarEmpresasDesincronizadasResponse> {
    return api.get<ListarEmpresasDesincronizadasResponse>('/sysadmin/empresas/desincronizadas');
  },

  async obtenerEmpresasConHorasDesincronizadas(): Promise<ListarEmpresasHorasDesincronizadasResponse> {
    return api.get<ListarEmpresasHorasDesincronizadasResponse>('/sysadmin/empresas/horas-desincronizadas');
  },

  async recalcularHorasEmpresa(nit: string): Promise<{ success: boolean; nit: string }> {
    return api.post<{ success: boolean; nit: string }>(`/sysadmin/empresas/${nit}/recalcular-horas`);
  },

  async obtenerAsesoriasFinalizadasConAudiosRechazados(): Promise<ListarAsesoriasHorasPorRechazoResponse> {
    return api.get<ListarAsesoriasHorasPorRechazoResponse>('/sysadmin/asesorias/horas-por-rechazo');
  },

  async corregirHorasAsesoriaPorRechazo(asesoriaId: number): Promise<CorregirHorasAsesoriaPorRechazoResponse> {
    return api.post<CorregirHorasAsesoriaPorRechazoResponse>(`/sysadmin/asesorias/${asesoriaId}/corregir-horas-por-rechazo`);
  },

  async obtenerAsesoriasSinEvaluacionAprobada(): Promise<ListarAsesoriasSinEvaluacionAprobadaResponse> {
    return api.get<ListarAsesoriasSinEvaluacionAprobadaResponse>('/sysadmin/asesorias/sin-evaluacion-aprobada');
  },

  // No-FINALIZADA sin ningún requisito ni terna evaluados (nunca se tocó la matriz).
  async obtenerAsesoriasSinEvaluacion(): Promise<{ success: boolean; data: AsesoriaSinActaDto[] }> {
    return api.get('/sysadmin/asesorias/sin-evaluacion');
  },

  // No-FINALIZADA con evaluación en la matriz pero sin ningún acta (Evaluacion) — bloquean aprobarAsesoria.
  async obtenerAsesoriasSinActa(): Promise<{ success: boolean; data: AsesoriaSinActaDto[] }> {
    return api.get('/sysadmin/asesorias/sin-acta');
  },

  async generarActaFaltante(asesoriaId: number): Promise<GenericResponse> {
    return api.post<GenericResponse>(`/sysadmin/asesorias/${asesoriaId}/generar-acta`);
  },

  // Regeneración de actas existentes (Opción 2) — cuenta cuántas actas con actaLink cumplen los
  // filtros (no excluye las ya regeneradas: eso solo se sabe leyendo cada documento en Drive, y el
  // batch ya las salta solas). Disparar la regeneración en sí se hace vía useBackgroundJob().startJob().
  async obtenerActasPendientesRegeneracion(filtros?: { empresaIds?: string[] }): Promise<{ success: boolean; total: number }> {
    const qs = filtros?.empresaIds?.length ? `?empresaIds=${encodeURIComponent(filtros.empresaIds.join(','))}` : '';
    return api.get(`/sysadmin/actas/regenerar/pendientes${qs}`);
  },

  // Estado de la corrida en curso. Vive en el backend, no en el navegador: por eso el botón sigue
  // bloqueado aunque se recargue la página o se abra el panel desde otro equipo.
  async obtenerEstadoRegeneracion(): Promise<{
    success: boolean;
    estado: EstadoRegeneracionDto | null;
    ultimaEjecucion: ResumenEjecucionDto | null;
  }> {
    return api.get('/sysadmin/actas/regenerar/estado');
  },

  // Migración de la fila AUDIOS (horas y tabla) a actas ya transformadas
  async previsualizarMigracionHoras(filtros?: {
    empresaIds?: string[];
    asesoriaIds?: number[];
    limit?: number;
  }): Promise<ResumenMigrarHorasDto> {
    return api.post('/sysadmin/actas/aplicar-horas/preview', filtros ?? {});
  },

  async obtenerEstadoMigracionHoras(): Promise<{
    success: boolean;
    estado: EstadoMigrarHorasServidorDto | null;
    ultimaEjecucion: ResumenMigrarHorasDto | null;
  }> {
    return api.get('/sysadmin/actas/aplicar-horas/estado');
  },

  async cancelarSimulacionHoras(): Promise<{ success: boolean }> {
    return api.post('/sysadmin/actas/aplicar-horas/cancelar', {});
  },

  // Correcciones de asesorías: audios subidos a la asesoría equivocada / por otro asesor y asesorías sin audios.
  async obtenerCasosCorreccion(): Promise<CasosCorreccionResponse> {
    return api.get('/sysadmin/correcciones/casos');
  },

  async cambiarDuenoAsesoria(asesoriaId: number, asesorId: number): Promise<CambiarDuenoResponse> {
    return api.patch(`/sysadmin/correcciones/asesorias/${asesoriaId}/asesor`, { asesorId });
  },

  async moverAudioAsesoria(audioId: number, asesoriaId: number): Promise<MoverAudioResponse> {
    return api.post(`/sysadmin/correcciones/audios/${audioId}/mover`, { asesoriaId });
  },

  // Asesorías intermedias, absorber y editar orden/objetivo (con acta).
  async obtenerAsesoriaParaEdicion(asesoriaId: number): Promise<AsesoriaEdicionResponse> {
    return api.get(`/sysadmin/correcciones/asesorias/${asesoriaId}`);
  },

  async crearAsesoriaIntermedia(
    asesoriaId: number,
    body: { audioIds: number[]; asesorId?: number; objetivo?: string }
  ): Promise<CrearIntermediaResponse> {
    return api.post(`/sysadmin/correcciones/asesorias/${asesoriaId}/intermedia`, body);
  },

  async absorberAsesoria(asesoriaId: number, body: { destinoId: number; audioIds?: number[] }): Promise<AbsorberResponse> {
    return api.post(`/sysadmin/correcciones/asesorias/${asesoriaId}/absorber`, body);
  },

  async editarAsesoria(
    asesoriaId: number,
    body: { orden?: number; objetivo?: string; actualizarActa?: boolean }
  ): Promise<EditarAsesoriaResponse> {
    return api.patch(`/sysadmin/correcciones/asesorias/${asesoriaId}`, body);
  },

  // Prueba de la fila AUDIOS: copia el acta real de la asesoría y le agrega la fila (no toca BD ni el original).
  async probarAudiosEnActa(asesoriaId: number): Promise<PruebaAudiosActaResponse> {
    return api.post(`/sysadmin/actas/prueba-audios/${asesoriaId}`, {});
  },

  async eliminarCopiaPruebaActa(fileId: string): Promise<{ success: boolean }> {
    return api.delete(`/sysadmin/actas/prueba-audios/copias/${encodeURIComponent(fileId)}`);
  },

  // Empresas con 2+ asesorías EN_CURSO simultáneas — estado que ningún guard del sistema espera.
  async obtenerEmpresasConDosEnCurso(): Promise<{ success: boolean; data: EmpresaConDosEnCursoDto[] }> {
    return api.get('/sysadmin/asesorias/dos-en-curso');
  },

  async corregirDosEnCurso(nit: string): Promise<{ success: boolean; corregidas: number }> {
    return api.post(`/sysadmin/empresas/${nit}/corregir-dos-en-curso`);
  },

  // --- Google Drive ---
  async copyDriveFileAndShare(data: CopyDriveFileRequest): Promise<CopyDriveFileResponse> {
    return api.post<CopyDriveFileResponse>('/sysadmin/drive/copy-share', data);
  },

  // --- Auditoría de Drive (evidencias/audios eliminados o huérfanos) ---
  async auditarEvidenciasEliminadas(): Promise<ListarEvidenciasEliminadasAuditoriaResponse> {
    return api.get<ListarEvidenciasEliminadasAuditoriaResponse>('/sysadmin/drive-audit/evidencias-eliminadas');
  },

  async auditarCarpetaEmpresa(nit: string): Promise<AuditoriaCarpetaEmpresaResponse> {
    return api.get<AuditoriaCarpetaEmpresaResponse>(`/sysadmin/drive-audit/empresas/${nit}/carpeta`);
  },

  async listarPapeleraEmpresa(nit: string): Promise<AuditoriaCarpetaEmpresaResponse> {
    return api.get<AuditoriaCarpetaEmpresaResponse>(`/sysadmin/drive-audit/empresas/${nit}/papelera`);
  },

  // Envía un archivo a la papelera de Drive (reversible — usa trashed:true, no borrado permanente).
  async eliminarArchivoDrive(fileId: string): Promise<{ success: boolean; message?: string }> {
    return api.post(`/sysadmin/drive-audit/archivos/${fileId}/eliminar`);
  },

  // Recupera un archivo de la papelera de Drive. Solo funciona si Google aún no lo purgó.
  async recuperarArchivoDrive(fileId: string): Promise<{ success: boolean; message?: string }> {
    return api.post(`/sysadmin/drive-audit/archivos/${fileId}/recuperar`);
  },

  async listarCarpetasEmpresaDrive(nit: string): Promise<ListarCarpetasEmpresaResponse> {
    return api.get<ListarCarpetasEmpresaResponse>(`/sysadmin/drive-audit/empresas/${nit}/carpetas`);
  },

  async moverArchivosDrive(data: MoverArchivosDriveRequest): Promise<MoverArchivosDriveResponse> {
    return api.post<MoverArchivosDriveResponse>('/sysadmin/drive-audit/archivos/mover', data);
  },

  // --- Export / Import Cumplimiento ---
  async exportComplianceData(): Promise<{ success: boolean; data: unknown[] }> {
    return api.get<{ success: boolean; data: unknown[] }>('/sysadmin/cumplimiento/export');
  },

  async importComplianceData(data: unknown[]): Promise<{ success: boolean; updated: number }> {
    return api.post<{ success: boolean; updated: number }>('/sysadmin/cumplimiento/import', { data });
  },

  async migrarCompromisosConclusiiones(): Promise<{ success: boolean; migradas: number; omitidas: number }> {
    return api.post<{ success: boolean; migradas: number; omitidas: number }>('/sysadmin/asesorias/migrar-compromisos');
  },

  async backfillOrdenAsesorias(): Promise<{ success: boolean; data: { actualizadas: number } }> {
    return api.post<{ success: boolean; data: { actualizadas: number } }>('/sysadmin/asesorias/backfill-orden', {});
  },

  // --- Indicadores de Calidad ---
  async listarIndicadoresBase(): Promise<ListarIndicadoresBaseResponse> {
    return api.get<ListarIndicadoresBaseResponse>('/sysadmin/indicadores-base');
  },

  async actualizarIndicadorBase(
    id: number,
    data: {
      nombre?: string;
      formula?: string;
      frecuencia?: 'MENSUAL' | 'TRIMESTRAL' | 'SEMESTRAL' | 'ANUAL';
      fuenteDatos?: string;
      meta?: number;
      tipoResultado?: 'PORCENTAJE' | 'NUMERO' | 'MONEDA';
    }
  ): Promise<ActualizarIndicadorBaseResponse> {
    return api.patch<ActualizarIndicadorBaseResponse>(`/sysadmin/indicadores-base/${id}`, data);
  },

  async crearIndicadorBase(data: {
    nombre: string;
    formula: string;
    frecuencia: 'MENSUAL' | 'TRIMESTRAL' | 'SEMESTRAL' | 'ANUAL';
    fuenteDatos: string;
    meta: number;
    tipoResultado: 'PORCENTAJE' | 'NUMERO' | 'MONEDA';
    variables: { nombre: string; orden?: number }[];
  }): Promise<CrearIndicadorBaseResponse> {
    return api.post<CrearIndicadorBaseResponse>('/sysadmin/indicadores-base', data);
  },

  async eliminarIndicadorBase(id: number): Promise<{ success: boolean; message: string }> {
    return api.delete<{ success: boolean; message: string }>(`/sysadmin/indicadores-base/${id}`);
  },

  async listarEmpresasConAnomaliasNtc(): Promise<EmpresaAnomaliaDto[]> {
    const res = await api.get<{ success: boolean; empresas: EmpresaAnomaliaDto[] }>('/sysadmin/empresas/anomalias-ntc');
    return res.empresas;
  },

  // --- Google Drive File Management & Audios ---
  async listDriveFolderFiles(folderId: string, nit?: string): Promise<ListDriveFolderFilesResponse> {
    const query = nit ? `?nit=${encodeURIComponent(nit)}` : '';
    return api.get<ListDriveFolderFilesResponse>(`/sysadmin/drive/folders/${folderId}/files${query}`);
  },

  async renameDriveFile(fileId: string, newName: string): Promise<RenameDriveFileResponse> {
    return api.patch<RenameDriveFileResponse>(`/sysadmin/drive/files/${fileId}/rename`, { newName });
  },

  async batchRenameDriveFiles(files: Array<{ fileId: string; newName: string }>): Promise<BatchRenameDriveFilesResponse> {
    return api.post<BatchRenameDriveFilesResponse>('/sysadmin/drive/files/batch-rename', { files });
  },

  async autoNormalizeFolderAudios(folderId: string, apply = false): Promise<AutoNormalizeFolderAudiosResponse> {
    return api.post<AutoNormalizeFolderAudiosResponse>(`/sysadmin/drive/folders/${folderId}/normalize-audios`, { apply });
  },

  async detectFolderDuplicates(folderId: string, nit?: string): Promise<DetectFolderDuplicatesResponse> {
    const query = nit ? `?nit=${encodeURIComponent(nit)}` : '';
    return api.get<DetectFolderDuplicatesResponse>(`/sysadmin/drive/folders/${folderId}/duplicates${query}`);
  },

  async deleteDriveFile(fileId: string): Promise<DeleteDriveFileResponse> {
    return api.delete<DeleteDriveFileResponse>(`/sysadmin/drive/files/${fileId}`);
  },

  async batchDeleteDriveFiles(fileIds: string[]): Promise<DeleteDriveFilesBatchResponse> {
    return api.post<DeleteDriveFilesBatchResponse>('/sysadmin/drive/files/batch-delete', { fileIds });
  },
};

// Admin-accessible indicator management — calls /api/admin/* (ADMIN | SYSADMIN)
export const adminIndicadoresService = {
  async listar(): Promise<ListarIndicadoresBaseResponse> {
    return api.get<ListarIndicadoresBaseResponse>('/admin/indicadores-base');
  },
  async crear(data: {
    nombre: string;
    formula: string;
    frecuencia: 'MENSUAL' | 'TRIMESTRAL' | 'SEMESTRAL' | 'ANUAL';
    fuenteDatos: string;
    meta: number;
    tipoResultado: 'PORCENTAJE' | 'NUMERO' | 'MONEDA';
    variables: { nombre: string; orden?: number }[];
  }): Promise<CrearIndicadorBaseResponse> {
    return api.post<CrearIndicadorBaseResponse>('/admin/indicadores-base', data);
  },
  async actualizar(
    id: number,
    data: {
      nombre?: string;
      formula?: string;
      frecuencia?: 'MENSUAL' | 'TRIMESTRAL' | 'SEMESTRAL' | 'ANUAL';
      fuenteDatos?: string;
      meta?: number;
      tipoResultado?: 'PORCENTAJE' | 'NUMERO' | 'MONEDA';
    }
  ): Promise<ActualizarIndicadorBaseResponse> {
    return api.patch<ActualizarIndicadorBaseResponse>(`/admin/indicadores-base/${id}`, data);
  },
  async eliminar(id: number): Promise<{ success: boolean; message: string }> {
    return api.delete<{ success: boolean; message: string }>(`/admin/indicadores-base/${id}`);
  },
};

export const adminEmpresasService = {
  async listarTodas(): Promise<ListarEmpresasSysadminResponse> {
    return api.get<ListarEmpresasSysadminResponse>('/admin/empresas');
  },
};

export interface IndicadorBaseSysadmin {
  id: number;
  nombre: string;
  formula: string;
  frecuencia: 'MENSUAL' | 'TRIMESTRAL' | 'SEMESTRAL' | 'ANUAL';
  fuenteDatos: string;
  meta: number;
  tipoResultado: 'PORCENTAJE' | 'NUMERO' | 'MONEDA';
  variablesBase: { nombre: string; orden: number | null }[];
}

export interface ListarIndicadoresBaseResponse {
  success: boolean;
  baseIndicadores: IndicadorBaseSysadmin[];
}

export interface ActualizarIndicadorBaseResponse {
  success: boolean;
  baseIndicador: IndicadorBaseSysadmin;
}

export interface CrearIndicadorBaseResponse {
  success: boolean;
  baseIndicador: IndicadorBaseSysadmin;
}

export interface EmpresaAnomaliaDto {
  nitId: string;
  nombre: string;
  asesor: string | null;
  combinacion: string | null;
  cumplimientoCount: number;
  anomalias: { ntcId: number; nombre: string }[];
}

// ========================================
// TIPOS - Empresas Desincronizadas
// ========================================

export interface EmpresaDesincronizadaDto {
  nitId: string;
  nombre: string;
  porcentajeActual: number;
  porcentajeCalculado: number;
  diferenciaPorcentaje: number;
  asesoriasFinalizadasSinAprobar: number;
}

export interface ListarEmpresasDesincronizadasResponse {
  success: boolean;
  data: EmpresaDesincronizadaDto[];
}

// ========================================
// TIPOS - Empresas con Horas Desincronizadas
// ========================================

export interface EmpresaHorasDesincronizadaDto {
  nitId: string;
  nombre: string;
  horasActual: number;
  horasCalculadas: number;
  diferenciaHoras: number;
}

export interface ListarEmpresasHorasDesincronizadasResponse {
  success: boolean;
  data: EmpresaHorasDesincronizadaDto[];
}

// ========================================
// TIPOS - Asesorias FINALIZADA con audios rechazados (horas infladas)
// ========================================

export interface AsesoriaHorasPorRechazoDto {
  asesoriaId: number;
  empresaId: string;
  empresaNombre: string;
  horasActual: number;
  horasCalculadas: number;
  diferenciaHoras: number;
  audiosRechazados: number;
}

export interface ListarAsesoriasHorasPorRechazoResponse {
  success: boolean;
  data: AsesoriaHorasPorRechazoDto[];
}

export interface CorregirHorasAsesoriaPorRechazoResponse {
  success: boolean;
  empresaId: string;
  horasAntes: number;
  horasDespues: number;
}

// ========================================
// TIPOS - Asesorias sin evaluacion aprobada
// ========================================

export interface AsesoriaSinEvaluacionAprobadaDto {
  asesoriaId: number;
  nitId: string;
  nombreEmpresa: string;
  fechaFin: string | null;
  horas: number;
  evaluacionEstado: string | null;
}

export interface ListarAsesoriasSinEvaluacionAprobadaResponse {
  success: boolean;
  data: AsesoriaSinEvaluacionAprobadaDto[];
}

export interface UsoTokensDto {
  llamadas: number;
  inputTokens: number;
  /** Output facturable: incluye los "thinking tokens", que son la mayor parte del gasto. */
  outputTokens: number;
  totalTokens: number;
}

export interface ResumenEjecucionDto {
  progressId: string;
  iniciadoEn: string;
  finalizadoEn: string;
  duracionMs: number;
  totalElegibles: number;
  procesadas: number;
  regeneradas: number;
  saltadas: number;
  fallidas: number;
  conIA: number;
  sinIA: number;
  viejosSinRenombrar: number;
  msPromedioConIA: number;
  msPromedioSinIA: number;
  msPromedioPorActa: number;
  tokens: UsoTokensDto;
  costoUsd: number;
  costoCop: number;
  costoUsdPromedioPorActaConIA: number;
  filtros: { empresaIds?: string[]; asesoriaIds?: number[]; limit?: number; force?: boolean };
  empresas: number;
  errores: { asesoriaId: number; error: string }[];
  restantes: number;
  proyeccionCostoUsd: number;
  proyeccionMs: number;
}

export type EstadoMigracionItemDto =
  | 'APLICADA_OK'
  | 'DRY_RUN_OK'
  | 'SALTADA_PROBLEMA_AUDIOS'
  | 'SALTADA_NO_TRANSFORMADA'
  | 'SALTADA_YA_TIENE_AUDIOS'
  | 'SALTADA_SIN_EVIDENCIAS'
  | 'SALTADA_SIN_TABLA_AUDIOS'
  | 'LETRA_POR_CORREGIR'
  | 'LETRA_CORREGIDA'
  | 'ERROR';

/** AGREGAR_AUDIOS agrega la fila donde falte; CORREGIR_LETRA solo arregla la letra tamaño 1 de las tablas ya escritas. */
export type AccionMigrarHorasDto = 'AGREGAR_AUDIOS' | 'CORREGIR_LETRA';

export interface DetalleMigracionItemDto {
  asesoriaId: number;
  orden: number | null;
  empresaNit: string;
  empresaNombre: string;
  asesorId: number;
  asesorNombre: string;
  actaUrl: string;
  docId: string;
  docTitulo?: string;
  estado: EstadoMigracionItemDto;
  motivo?: string;
  totalAudios: number;
  duracionTotal: string;
  audiosSubieron?: string[];
}

export interface ResumenMigrarHorasDto {
  success: boolean;
  totalAnalizadas: number;
  aplicadas: number;
  dryRunOk: number;
  saltadasProblemaAudios: number;
  saltadasYaTenianAudios: number;
  saltadasNoTransformadas: number;
  saltadasSinEvidencias: number;
  sinAudiosTotalCero: number;
  /** Tablas de audios con letra tamaño 1: por corregir (simulación) o ya corregidas. */
  letraCorregida?: number;
  cancelada?: boolean;
  saltadasSinTablaAudios?: number;
  fallidas: number;
  duracionMs: number;
  dryRun: boolean;
  detalles: DetalleMigracionItemDto[];
}

export interface EstadoMigrarHorasServidorDto {
  enCurso: boolean;
  progressId: string;
  iniciadoEn: string;
  total: number;
  procesadas: number;
  aplicadas: number;
  saltadas: number;
  fallidas: number;
  asesoriaActual: number | null;
  dryRun: boolean;
  cancelacionSolicitada?: boolean;
}

export interface EstadoRegeneracionDto {
  enCurso: boolean;
  progressId: string;
  iniciadoEn: string;
  filtros: { empresaIds?: string[]; asesoriaIds?: number[]; limit?: number; force?: boolean };
  total: number;
  procesadas: number;
  ok: number;
  saltadas: number;
  fallidas: number;
  asesoriaActual: number | null;
}

export interface AsesoriaSinActaDto {
  asesoriaId: number;
  empresaId: string;
  empresaNombre: string;
  asesorId: number;
  asesorNombre: string;
  estado: string;
  fechaInicio: string;
  horas: number;
}

export interface EmpresaConDosEnCursoDto {
  empresaId: string;
  empresaNombre: string;
  asesorias: Array<{ asesoriaId: number; asesorId: number; asesorNombre: string; fechaInicio: string; estado: string }>;
}

// ========================================
// TIPOS - Auditoría de Drive (evidencias/audios eliminados o huérfanos)
// ========================================

export type EstadoDriveEvidenciaEliminada = 'TRASHED_OK' | 'NO_EXISTE' | 'SIGUE_VIVA' | 'URL_INVALIDA' | 'ERROR';

export interface EvidenciaEliminadaAuditoriaDto {
  evidenciaId: number;
  nombre: string;
  asesoriaId: number;
  empresaId: string;
  empresaNombre: string;
  fileId: string | null;
  driveStatus: EstadoDriveEvidenciaEliminada;
  detalle?: string;
  jobStatus: string;
  jobIntentos: number;
  jobError: string | null;
}

export interface ListarEvidenciasEliminadasAuditoriaResponse {
  success: boolean;
  data: EvidenciaEliminadaAuditoriaDto[];
}

export type CategoriaArchivoDrive = 'AUDIO' | 'EVIDENCIA' | 'ACTA' | 'OTRO';

export interface ArchivoDriveAuditoriaDto {
  id: string;
  name: string;
  mimeType: string;
  webViewLink?: string;
  path: string;
  reconocido: boolean;
  categoria: CategoriaArchivoDrive;
}

export interface AuditoriaCarpetaEmpresaResponse {
  success: boolean;
  message?: string;
  empresaId?: string;
  empresaNombre?: string;
  totalArchivos?: number;
  totalNoReconocidos?: number;
  archivos?: ArchivoDriveAuditoriaDto[];
}

export interface CarpetaDriveDto {
  id: string;
  name: string;
  path: string;
}

export interface ListarCarpetasEmpresaResponse {
  success: boolean;
  message?: string;
  carpetas?: CarpetaDriveDto[];
}

export interface MoverArchivosDriveRequest {
  fileIds: string[];
  targetFolderId?: string;
  parentFolderId?: string;
  nuevaCarpetaNombre?: string;
}

export interface MoverArchivosDriveResponse {
  success: boolean;
  message?: string;
  resultados?: Array<{ fileId: string; ok: boolean; error?: string }>;
}

export interface DriveFileItemDto {
  id: string;
  name: string;
  suggestedName?: string;
  mimeType?: string;
  size?: number;
  webViewLink?: string;
  /** Asesoría a la que pertenece el audio (cruzado con Audio.asesoriaId). Solo viene si se pidió con `nit`. */
  asesoriaId?: number;
  asesoriaLabel?: string;
}

export interface ListDriveFolderFilesResponse {
  success: boolean;
  folderId: string;
  total: number;
  files: DriveFileItemDto[];
}

export interface RenameDriveFileResponse {
  success: boolean;
  message: string;
  file: {
    id: string;
    previousName?: string;
    name: string;
  };
}

export interface BatchRenameDriveFilesResponse {
  success: boolean;
  total: number;
  renamed: Array<{
    fileId: string;
    newName: string;
    success: boolean;
    error?: string;
  }>;
}

export interface AutoNormalizeFolderAudiosResponse {
  success: boolean;
  total: number;
  isDryRun: boolean;
  items: Array<{
    fileId: string;
    currentName: string;
    targetName: string;
    status: string;
  }>;
}

export interface DuplicateFileItemDto {
  id: string;
  name: string;
  size?: number;
  md5Checksum?: string;
  createdTime?: string;
  webViewLink?: string;
  isOriginal: boolean;
  asesoriaId?: number;
  asesoriaLabel?: string;
}

export interface DuplicateGroupDto {
  key: string;
  criteria: 'md5' | 'name_size';
  count: number;
  files: DuplicateFileItemDto[];
}

export interface DetectFolderDuplicatesResponse {
  success: boolean;
  folderId: string;
  totalDuplicates: number;
  totalGroups: number;
  groups: DuplicateGroupDto[];
}

export interface DeleteDriveFileResponse {
  success: boolean;
  message: string;
  fileId: string;
}

export interface DeleteDriveFilesBatchResponse {
  success: boolean;
  total: number;
  deleted: Array<{
    fileId: string;
    success: boolean;
    error?: string;
  }>;
}

// --- Auditoría de nombres de audios ---
export type SeveridadProblema = 'error' | 'advertencia';

export interface ProblemaAuditoriaDTO {
  codigo: string;
  severidad: SeveridadProblema;
  mensaje: string;
}

export interface AudioAuditadoDTO {
  audioId: number;
  fileId: string | null;
  nombre: string | null;
  url: string | null;
  fechaGrabacion: string;
  duracionSeg: number;
  estado: string;
  formatoValido: boolean;
  nombreSugerido: string | null;
  problemas: ProblemaAuditoriaDTO[];
}

export interface AsesoriaAuditadaDTO {
  asesoriaId: number;
  orden: number | null;
  estado: string;
  fechaInicio: string;
  audios: AudioAuditadoDTO[];
  totalProblemas: number;
}

export interface EmpresaAuditadaDTO {
  nit: string;
  nombre: string;
  carpetaId: string | null;
  carpetaAudiosId: string | null;
  problemas: ProblemaAuditoriaDTO[];
  asesorias: AsesoriaAuditadaDTO[];
  huerfanos: { fileId: string; nombre: string; url: string | null; formatoValido: boolean }[];
  totalAudios: number;
  audiosConProblemas: number;
}

export interface AuditoriaNombresAudiosResponse {
  success: boolean;
  formato: string;
  generadoEn: string;
  resumen: { empresas: number; empresasConProblemas: number; audios: number; audiosConProblemas: number; huerfanos: number };
  empresas: EmpresaAuditadaDTO[];
}

// --- Prueba de fila AUDIOS en actas ---
export interface PruebaAudiosActaResponse {
  success: boolean;
  asesoriaId: number;
  actaOriginal: { id: string; nombre: string; url: string };
  copia: { id: string; nombre: string; url: string };
  filaInsertada: boolean;
  audios: { audioId: number; nombre: string; url: string; duracion: string; formatoValido: boolean }[];
  total: string;
  advertencias: string[];
}

// --- Correcciones de asesorías ---
export type TipoCasoCorreccion = 'AUDIOS_AJENOS' | 'FINALIZADA_SIN_AUDIOS' | 'REVISION_SIN_AUDIOS' | 'EN_CURSO_ANTIGUA';

export interface AsesoriaRefDTO {
  asesoriaId: number;
  empresa: string;
  estado: string;
  orden: number | null;
  fechaInicio: string;
  asesorId: number;
  audiosVigentes: number;
}

export interface AudioCasoDTO {
  audioId: number;
  estado: string;
  fechaGrabacion: string;
  duracionSeg: number;
  url: string | null;
  subioId: number;
  esDelDueno: boolean;
  candidatos: (AsesoriaRefDTO & { dias: number })[];
}

export interface CasoCorreccionDTO extends AsesoriaRefDTO {
  tipo: TipoCasoCorreccion;
  horas: number;
  titularEmpresaId: number | null;
  audiosNoVigentes: number;
  audios: AudioCasoDTO[];
}

export interface CasosCorreccionResponse {
  success: boolean;
  asesores: { asesorId: number; nombre: string; activo: boolean }[];
  casos: CasoCorreccionDTO[];
}

export interface CambiarDuenoResponse {
  success: boolean;
  asesoriaId: number;
  antes: { asesorId: number; nombre: string };
  despues: { asesorId: number; nombre: string };
}

export interface MoverAudioResponse {
  success: boolean;
  audioId: number;
  origen: { asesoriaId: number; empresaId: string; horas: number | null };
  destino: { asesoriaId: number; empresaId: string; horas: number | null };
  /** Horas enteras sumadas/restadas al total de cada empresa afectada. */
  horasEmpresa: Record<string, number>;
  archivoMovido: boolean;
  advertencias: string[];
}

// --- Asesorías intermedias / edición ---
export interface AsesoriaEmpresaDTO {
  asesoriaId: number;
  orden: number | null;
  nombreActividad: string | null;
  estado: string;
  objetivo: string;
  asesorId: number;
  fechaInicio: string;
  audios: number;
  tieneActa: boolean;
}

export interface AsesoriaEdicionResponse {
  success: boolean;
  asesoriaId: number;
  empresaId: string;
  empresa: string;
  orden: number | null;
  nombreActividad: string | null;
  objetivo: string;
  estado: string;
  horas: number;
  fechaInicio: string;
  asesorId: number;
  actaLink: string | null;
  /** Sin matriz, evidencias, reuniones ni acta: se puede vaciar y eliminar al absorberla. */
  soloAudios: boolean;
  esCorreccion: boolean;
  audios: { audioId: number; estado: string; duracionSeg: number; fechaGrabacion: string; subioId: number }[];
  asesoriasEmpresa: AsesoriaEmpresaDTO[];
}

export interface CrearIntermediaResponse {
  success: boolean;
  origenId: number;
  asesoriaId: number;
  asesorId: number;
  objetivo: string;
  orden: number;
  nombreActividad: string;
  audiosMovidos: number;
  horasEmpresa: Record<string, number>;
  advertencias: string[];
  desplazadas: { asesoriaId: number; ordenAntes: number; ordenDespues: number; tieneActa: boolean }[];
}

export interface AbsorberResponse {
  success: boolean;
  origenId: number;
  destinoId: number;
  audiosMovidos: number;
  origenEliminada: boolean;
  horasEmpresa: Record<string, number>;
  advertencias: string[];
}

export interface EditarAsesoriaResponse {
  success: boolean;
  asesoriaId: number;
  orden: number | null;
  objetivo: string;
  nombreActividad: string | null;
  acta: { actualizada: boolean; renombrada: boolean; advertencias: string[] };
}
